import React, { ComponentProps } from 'react';
import {
  TextStyle,
  TouchableOpacity,
  StyleSheet,
  Platform,
} from 'react-native';

import { Text, Box } from '@components//Restyle';
import Icon from '@components/shared/Icon/Icon';
import { IconSizes } from '@components/shared/Icon/RestyleIcon';
import images from '@themes/images';
import theme from '@themes/theme';

enum RadioTextType {
  body = 'body',
  labelEmphasized = 'labelEmphasized',
}

type RadioButtonType = {
  onPress: () => void;
  isSelected: boolean;
  text?: string;
  iconVariant?: IconSizes;
  textVariant?: keyof typeof theme.textVariants;
  textColor?: keyof typeof theme.colors;
  textStyle?: TextStyle;
  backgroundColor?: string;
  stateIconColor?: keyof typeof theme.colors;
  disabled?: boolean;
  testID?: string;
  iconDefault?: keyof typeof images;
  iconColor?: keyof typeof theme.colors;
  isTaskAssignee?: boolean;
};

const RadioButton = ({
  onPress,
  isSelected,
  text,
  iconVariant = 'l',
  textVariant,
  textColor,
  textStyle = {
    paddingLeft: 8,
  },
  backgroundColor = 'transparent',
  stateIconColor = 'grey03',
  disabled,
  testID,
  iconDefault,
  iconColor,
  isTaskAssignee = false,
}: RadioButtonType) => {
  const _textVariant =
    textVariant === 'labelEmphasized'
      ? RadioTextType.labelEmphasized
      : RadioTextType.body;

  const name = (): ComponentProps<typeof Icon>['name'] => {
    if (disabled) {
      return 'StateDisabled';
    }

    if (isSelected) {
      return 'StateYes';
    }

    return 'StateNo';
  };

  const isSelectedColor = disabled ? 'grey04' : 'greenSecondary';

  const selectedColorForNotTaskAssignee =
    disabled && Platform.OS === 'web' ? 'grey04' : 'greenSecondary';

  return (
    <TouchableOpacity
      testID={testID}
      onPress={onPress}
      accessibilityState={{ disabled: disabled }}
      disabled={disabled}
      accessibilityLabel={disabled ? 'Selected' : 'Unselected'}>
      <Box accessibilityLabel={text} flexDirection='row' alignItems='center'>
        {isTaskAssignee && (
          <Box
            style={{
              position: 'relative',
              backgroundColor: backgroundColor,
              borderRadius: 100,
            }}
            opacity={1}>
            {disabled && (
              <Icon
                name={name()}
                variant={iconVariant}
                color='grey02'
                disabled={disabled}
                style={{ ...StyleSheet.absoluteFillObject }}
              />
            )}
            <Icon
              name={
                (disabled && !isSelected) || !isSelected
                  ? iconDefault ?? 'StateNo'
                  : 'StateYes'
              }
              variant={iconVariant}
              color={isSelected ? isSelectedColor : iconColor ?? stateIconColor}
              onPress={onPress}
              disabled={disabled}
            />
          </Box>
        )}
        {!isTaskAssignee && (
          <Box
            style={{
              position: 'relative',
              backgroundColor: backgroundColor,
              borderRadius: 100,
            }}
            opacity={disabled && isSelected && Platform.OS !== 'web' ? 0.5 : 1}>
            {disabled && (
              <Icon
                name={name()}
                variant={iconVariant}
                color='grey02'
                disabled={disabled}
                style={{ ...StyleSheet.absoluteFillObject }}
              />
            )}
            <Icon
              name={
                (disabled && !isSelected) || !isSelected
                  ? iconDefault ?? 'StateNo'
                  : 'StateYes'
              }
              variant={iconVariant}
              color={
                isSelected
                  ? selectedColorForNotTaskAssignee
                  : iconColor ?? stateIconColor
              }
              onPress={onPress}
              disabled={disabled}
            />
          </Box>
        )}
        {text && (
          <Text
            numberOfLines={1}
            variant={_textVariant}
            color={textColor}
            style={textStyle}>
            {text}
          </Text>
        )}
      </Box>
    </TouchableOpacity>
  );
};

export default RadioButton;
